import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { Menu, X } from "lucide-react";

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const isHome = location.pathname === "/";
  const transparentMode = isHome && !scrolled;

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${
      isActive
        ? "text-terracotta"
        : transparentMode
        ? "text-sand-50/90 hover:text-sand-50"
        : "text-charcoal hover:text-terracotta"
    }`;

  const logoColor = transparentMode ? "text-sand-50" : "text-charcoal";
  const langInactive = transparentMode ? "text-sand-50/70 hover:text-sand-50" : "text-charcoal-400 hover:text-charcoal";
  const langActive = transparentMode ? "text-sand-50 font-semibold" : "text-terracotta font-semibold";
  const dividerCls = transparentMode ? "text-sand-50/40" : "text-charcoal/30";
  const mobileBtnCls = transparentMode ? "text-sand-50" : "text-charcoal";

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-sand-50/85 border-b border-charcoal/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className={`font-serif-display text-2xl md:text-3xl tracking-tight transition-colors ${logoColor}`}>
            Khalfan
          </span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-terracotta mt-1.5">
            Oman
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/tours" className={linkCls} data-testid="nav-tours">{t.nav.tours}</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">{t.nav.about}</NavLink>
          <NavLink to="/gallery" className={linkCls} data-testid="nav-gallery">{t.nav.gallery}</NavLink>
          <NavLink to="/stories" className={linkCls} data-testid="nav-stories">{t.nav.stories}</NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">{t.nav.contact}</NavLink>
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div className="hidden sm:flex items-center gap-1 text-xs tracking-widest">
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition ${lang === "en" ? langActive : langInactive}`}
            >EN</button>
            <span className={dividerCls}>/</span>
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`px-2 py-1 transition ${lang === "fr" ? langActive : langInactive}`}
            >FR</button>
          </div>
          <Link
            to="/contact"
            data-testid="nav-book-btn"
            className="hidden md:inline-flex bg-terracotta hover:bg-terracotta-600 text-sand-50 rounded-full px-5 py-2.5 text-sm transition-all"
          >
            {t.nav.book}
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className={`md:hidden p-2 -mr-2 ${mobileBtnCls}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-sand-50 border-t border-charcoal/10 px-6 py-6 space-y-5">
          <NavLink to="/tours" className="block text-charcoal" data-testid="mobile-nav-tours">{t.nav.tours}</NavLink>
          <NavLink to="/about" className="block text-charcoal" data-testid="mobile-nav-about">{t.nav.about}</NavLink>
          <NavLink to="/gallery" className="block text-charcoal" data-testid="mobile-nav-gallery">{t.nav.gallery}</NavLink>
          <NavLink to="/stories" className="block text-charcoal" data-testid="mobile-nav-stories">{t.nav.stories}</NavLink>
          <NavLink to="/contact" className="block text-charcoal" data-testid="mobile-nav-contact">{t.nav.contact}</NavLink>
          <div className="flex items-center gap-2 pt-2 text-sm">
            <button onClick={() => setLang("en")} className={`px-3 py-1.5 rounded-full border ${lang === "en" ? "bg-charcoal text-sand-50 border-charcoal" : "border-charcoal/20"}`}>EN</button>
            <button onClick={() => setLang("fr")} className={`px-3 py-1.5 rounded-full border ${lang === "fr" ? "bg-charcoal text-sand-50 border-charcoal" : "border-charcoal/20"}`}>FR</button>
          </div>
          <Link to="/contact" className="block w-full text-center bg-terracotta text-sand-50 rounded-full py-3 mt-3">{t.nav.book}</Link>
        </div>
      )}
    </header>
  );
}
